<script setup>
import {Chart} from 'highcharts-vue'
import Highcharts from 'highcharts'
import exportingInit from "highcharts/modules/exporting"
import addNetworkgraph from "highcharts/modules/networkgraph"
import dashboardService from "@/services/dashboard.service";
import {getLogo} from "@/helpers/logo";
import Vue, {ref, reactive, inject, getCurrentInstance, nextTick, watch, computed} from "vue";
import partnershipsGraphOptions from './partnershipsGraphOptions';
import partnershipsBubbleChartOptions from "./partnershipsBubbleChartOptions";
import useDebounce from "@/composables/useDebounce";
import PeriodFilter from "@/views/market/PeriodFilter.vue";
import ViewSelect from "@/views/market/ViewSelect.vue";
import PartnershipsModal from "@/components/partnerships/PartnershipsModal.vue";
import marketsService from "@/services/markets.service";

typeof exportingInit === 'function' && exportingInit(Highcharts);
typeof addNetworkgraph === 'function' && addNetworkgraph(Highcharts);

const instance = getCurrentInstance();

window.addToTracker = function (startupId) {
    instance.proxy.$emit('addToTracker', startupId);
};

window.showSimilar = function (startupId) {
    instance.proxy.$emit('showSimilar', startupId);
};

window.showPartnerships = function (data) {
    partnershipsModal.value.open(data);
};

const props = defineProps(['searchType','searchObjectId','startupsNodes']);

Highcharts.AST.allowedAttributes.push('onclick');
const debounce = useDebounce();

const viewTypes = [
    {label: 'Cluster View', value: 'clusters'},
    {label: 'Focus Area View ', value: 'focusAreas'},
]

const periods = [
    {label: 'All time', value: 'all'},
    {label: 'Current year', value: 'current'},
    {label: 'Last year', value: 'last'},
    {label: 'Last 3 years', value: 'last3'},
    {label: 'Custom period', value: 'custom'},
]

Vue.directive('esc', {
    bind(el, binding) {
        el._escHandler = function (e) {
            if (e.key === 'Escape' || e.keyCode === 27) {
                binding.value && binding.value(e);
            }
        };
        document.addEventListener('keydown', el._escHandler);
    },
    unbind(el) {
        document.removeEventListener('keydown', el._escHandler);
        delete el._escHandler;
    }
});

const graphOptions = reactive(partnershipsGraphOptions);
const bubbleChartOptions = reactive(partnershipsBubbleChartOptions);
const partnershipsCount = ref(inject('partnershipsCount'));
const startupsIds = inject('startupsIds');

const companiesForFilter = ref([]);
const watchCompaniesFilter = ref(true);
const companySearch = ref('');
const viewType = ref(viewTypes[1].value);
const period = ref(periods[0]);
const customPeriod = ref([null, null]);
const showCompaniesFilterList = ref(false);
const showPeriodsFilter = ref(false);
const showViewSelect = ref(false);
const graph = ref(null);
const chartSeries = ref([]);
const partnershipsModal = ref(null);

const chartOptions = computed(() => {
    let options = {
        ...(viewType.value === 'focusAreas' ? bubbleChartOptions : graphOptions),
        series: chartSeries.value,
    }

    if (viewType.value === 'focusAreas') {
        options.plotOptions.packedbubble.point.events.click = function () {
            partnershipsModal.value.open({type: 'focus_area', id: this.id, name: this.focus_area_name});
        };
    }

    return options;
});

const maxCompaniesInFilter = 25;
const selectedCompanies = computed(() => companiesForFilter.value.filter(item => item.checked));

const currentPeriodLabel = computed(() => {
    if (period.value.value === 'custom' && customPeriod.value[0] && customPeriod.value[1]) {
        return `${customPeriod.value[0]} - ${customPeriod.value[1]}`;
    }

    return periods.find(item => item.value === period.value.value).label;
});

const currentViewTypeLabel = computed(() => {
    return viewTypes.find(item => item.value === viewType.value).label;
});

const selectAll = computed({
    get() {
        return companiesForFilter.value.every(item => item.checked);
    },

    set(value) {
        companiesForFilter.value.forEach(item => {
            item.checked = value;
        });
    }
});

const hasSearchResults = computed(() => {
    return companiesForFilter.value.some(company => company.name.toLowerCase().includes(companySearch.value.toLowerCase()));
});

const filters = computed(() => {
    let filters = {
        ...props,
        years: period.value.value,
        customPeriod: customPeriod.value,
        viewType: viewType.value,
    };

    if (companiesForFilter.value.length) {
        filters.companiesIds = companiesForFilter.value.filter(item => item.checked).map(item => item.id);
    }

    return filters;
})

watch(companiesForFilter, () => {
    if (watchCompaniesFilter.value) {
        debounce(() => getData(), 1000);
    }
}, {deep: true});

watch(viewType, () => {
    reset();
});

watch(props, () => {
    reset();
}, {immediate: true, deep: true});

function reset() {
    partnershipsCount.value = 0;
    setCompaniesFilter([]);
    getData()
}

async function getData() {
    chartSeries.value = [];

    const {data} = await dashboardService.getChartData('PartnershipsMap', filters.value);

    let series = data.series;

    if (viewType.value === 'focusAreas') {
        chartSeries.value = series;
    } else {
        series.nodes = series.nodes.map(node => {
            return {
                ...node,
                marker: 'symbol' in node.marker ? {
                    ...node.marker,
                    symbol: 'url(' + getLogo(node.marker.symbol) + ')'
                } : node.marker
            }
        });

        series.stickyTracking = false;
        chartSeries.value.push(series);
    }

    partnershipsCount.value = data.partnerships_count;
    startupsIds.value = data.startups_ids;

    if (!companiesForFilter.value.length) {
        setCompaniesFilter(data.companies_for_filter.map((item, i) => ({
            ...item,
            checked: i < maxCompaniesInFilter,
        })));
    }
}

function setCompaniesFilter(value) {
    watchCompaniesFilter.value = false;
    companiesForFilter.value = value;

    nextTick(() => watchCompaniesFilter.value = true);
}

function setPeriod([value, customValue]) {
    period.value = value;
    customPeriod.value = customValue;
    showPeriodsFilter.value = false;

    getData();
}

function setViewType(value) {
    viewType.value = value;
    showViewSelect.value = false;
}

function hideTooltip() {
    const ch = graph.value && graph.value.chart;

    if (ch.tooltip && typeof ch.tooltip.hide === 'function') {
        ch.tooltip.hide();
    }
}

</script>

<template>
    <div>
        <div style="padding: 10px">
            <div class="graph-container__top">
                <div>
                    <div v-if="searchType === 'subsector'" class="filters__list-market" v-click-outside="() => {showViewSelect = false}">
                        <a class="filters__list__title" @click="showViewSelect = !showViewSelect">{{currentViewTypeLabel}}</a>

                        <b-collapse v-model="showViewSelect" class="filters__list__content" style="position: absolute;">
                            <ViewSelect
                                :view-types="viewTypes"
                                @apply="setViewType"
                            />
                        </b-collapse>
                    </div>
                </div>

                <div style="display: flex; justify-content: center;">
                    <div class="filters__list-market" v-click-outside="() => {showPeriodsFilter = false}">
                        <a class="filters__list__title" @click="showPeriodsFilter = !showPeriodsFilter">{{currentPeriodLabel}}</a>

                        <b-collapse v-model="showPeriodsFilter" class="filters__list__content" style="position: absolute;">
                            <PeriodFilter
                                :periods="periods"
                                @apply="setPeriod"
                            />
                        </b-collapse>
                    </div>

                    <div
                        v-if="companiesForFilter.length"
                        style=""
                        class="filters__list-market ml-2"
                        v-click-outside="() => {showCompaniesFilterList = false}"
                    >
                        <a class="filters__list__title" @click="showCompaniesFilterList = !showCompaniesFilterList">
                            {{selectedCompanies.length}} of {{companiesForFilter.length}}

                            <template v-if="startupsNodes">
                                startups
                            </template>

                            <template v-else>
                                enterprises
                            </template>
                        </a>

                        <b-collapse v-model="showCompaniesFilterList" class="filters__list__content" style="position: absolute;">
                            <div class="filters__list-market__search">
                                <input :placeholder="`Search for ${startupsNodes ? 'startups' : 'enterprises'}`" type="text" v-model="companySearch">
                            </div>
                            <ul class="filters__list__choose" style="max-height: 300px; overflow-y: auto;">
                                <li
                                    v-for="(company, index) in companiesForFilter"
                                    v-show="company.name.toLowerCase().includes(companySearch.toLowerCase())"
                                    :key="company.id"
                                    class="input-checkbox"
                                >
                                    <input :id="`filter-company-${company.id}`" type="checkbox" v-model="companiesForFilter[index].checked">
                                    <label :for="`filter-company-${company.id}`">{{company.name}}</label>
                                </li>
                            </ul>
                            <div v-if="!hasSearchResults">
                                No Results
                            </div>

                            <footer class="filters__list__content__footer">
                                <div class="input-checkbox">
                                    <input type="checkbox" v-model="selectAll" id="fic">
                                    <label for="fic" class="m-0">Select All</label>
                                </div>
                            </footer>
                        </b-collapse>
                    </div>
                </div>
            </div>
        </div>

        <Chart
            v-if="partnershipsCount"
            ref="graph"
            v-esc="hideTooltip"
            :options="chartOptions"
        />

        <div v-else class="graph-partnership-empty">
            <p>No partnerships in the selected period</p>
        </div>

        <PartnershipsModal
            ref="partnershipsModal"
            :get-partnerships-end-point="marketsService.getPartnershipsIndex"
            :filters="filters"
        />
    </div>
</template>

<style scoped>
    .graph-container__top {
        position: relative;
        z-index: 2;
        width: 100%;
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        align-items: center; 
        gap: 10px;
        margin-top: 7px;
        margin-right: 30px;
    }
    @media (min-width: 1025px) {
        .graph-container__top {
            position: absolute;
            padding-right: 50px;
            padding-left: 10px;
        }
    }
</style>
